import { Announcement } from '@/types'
import { AnnouncementSeverity } from '@/enums'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { getAnnouncements } from '@/api'

const DISMISSED_ANNOUNCEMENTS_KEY = 'dismissedAnnouncements'

const severityOrder: Record<AnnouncementSeverity, number> = {
  [AnnouncementSeverity.Critical]: 0,
  [AnnouncementSeverity.Warning]: 1,
  [AnnouncementSeverity.Information]: 2,
}

const getDismissedIds = (): number[] => {
  try {
    return JSON.parse(localStorage.getItem(DISMISSED_ANNOUNCEMENTS_KEY) || '[]')
  } catch {
    return []
  }
}

export const useAnnouncementStore = defineStore('announcement', () => {
  const announcements = ref<Announcement[]>([])
  const activeIndex = ref(0)

  const current = computed(() => announcements.value[activeIndex.value])

  let loadAnnouncementsPromise: Promise<void> | null = null
  const loadAnnouncements = async () => {
    if (loadAnnouncementsPromise) return loadAnnouncementsPromise

    loadAnnouncementsPromise = (async () => {
      const dismissedIds = getDismissedIds()
      const data = await getAnnouncements()
      announcements.value = data
        .filter((a) => !dismissedIds.includes(a.id))
        .sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity])
      activeIndex.value = 0
    })()

    try {
      await loadAnnouncementsPromise
    } finally {
      loadAnnouncementsPromise = null
    }
  }

  const dismissCurrent = () => {
    const item = current.value
    if (!item) return

    const dismissedIds = getDismissedIds()
    localStorage.setItem(DISMISSED_ANNOUNCEMENTS_KEY, JSON.stringify([...dismissedIds, item.id]))

    announcements.value = announcements.value.filter((a) => a.id !== item.id)
    if (activeIndex.value >= announcements.value.length) {
      activeIndex.value = Math.max(0, announcements.value.length - 1)
    }
  }

  const clear = () => {
    announcements.value = []
    activeIndex.value = 0
  }

  return {
    announcements,
    activeIndex,
    current,
    loadAnnouncements,
    dismissCurrent,
    clear,
  }
})
